import { useCallback, useEffect, useRef } from 'react';

import { API_STATUS, ApiStatus } from '@/constants';
import { GUEST_SURVIVE_ERROR_TYPE, useGuestSurviveApi } from '@/features/api';
import { useGuestDisplay } from '@/hooks/useGuestDisplay';
import { useInterval } from '@/hooks/useInterval';
import { GUEST_STATUS_TYPE } from '@/states/slices/guestDisplaySlice';
import { getGuestId } from '@/utils/guest';

/**
 * 実行間隔(ミリ秒)
 */
const POLLING_TIME = 300000; // 5分

/**
 * プロパティ
 */
type Props = {
  urlKey: string;
};

/**
 * 生存信号送信
 *
 * @param param0
 */
export const usePolling = ({ urlKey }: Props) => {
  const { surviveGuest, guestSurviveState } = useGuestSurviveApi();
  const { setGuestStatus } = useGuestDisplay();

  /**
   * ゲスト生存受信API呼び出し状態をRefで管理
   */
  const guestSurviveApiStateRef = useRef<ApiStatus>(guestSurviveState.status);
  useEffect(() => {
    guestSurviveApiStateRef.current = guestSurviveState.status;
  }, [guestSurviveState.status]);

  /**
   * 生存信号を送信
   */
  const sendSurvivalSignal = useCallback(() => {
    if (guestSurviveApiStateRef.current === API_STATUS.LOADING) {
      // すでに送信中の場合は何もしない
      return;
    }

    // ゲスト生存受信API呼び出し
    surviveGuest({ urlKey, guestId: getGuestId() });
  }, [surviveGuest, urlKey]);

  /**
   * 定期実行開始
   */
  const { stopTimer } = useInterval({
    onUpdate: sendSurvivalSignal,
    interval: POLLING_TIME,
    autoStart: true, // 即時実行
  });

  /**
   * ゲスト生存受信APIの呼び出し結果を監視
   */
  useEffect(() => {
    if (guestSurviveState.status !== API_STATUS.FAILED) {
      return; // 成功時も何もしない
    }

    if (
      guestSurviveState.errorType === GUEST_SURVIVE_ERROR_TYPE.REQUEST_ERROR
    ) {
      return; // オフライン時などリクエスト自体に失敗した場合は何もしない
    }

    // 定期実行停止
    stopTimer();

    // APIからOK以外が返却された場合は404エラー画面を表示
    setGuestStatus(GUEST_STATUS_TYPE.INVALID_URL_KEY);
  }, [guestSurviveState, setGuestStatus, stopTimer]);

  /**
   * アンマウント時
   */
  useEffect(
    () => () => {
      // 定期実行停止
      stopTimer();
    },
    [stopTimer],
  );
};
